/**
 * @module formatCurrency
 */
import {
  getCurrencyConfig,
  SYMBOL_POSITION,
  DEFAULT_SEPARATOR_PATTERN,
  CurrencyConfig,
} from '../config';
import { formatNumber } from '../formatNumber';
import { Code, Precision, ParsableNumber } from '../typings';
import { ifNumberValidThen } from '../utils';

/**
 * @typedef {Object} FormatCurrencyNumberOptions
 * @prop {?string} symbol Currency symbol. If provided, it will overwrite default currency symbol.
 * @prop {?number} precision Decimal places. If set false or null, it will keep number's original decimal places.
 * @prop {?number} maxPrecision	Maximum decimal places. If set false or null, it will not affect precision.
 * @prop {?boolean} noSeparator Set to true, it will not put separator into formatted number.
 */
/**
 * Format number with currency config. Return number only.
 * Can be replaced by `formatCurrency(code, input, { symbol: '', ...otherOptions })`, since *2.0.0-beta.9*.
 * @private
 * @deprecated
 * @since 2.0.0
 * @param {string} code 			Can be country code or currency ID
 * @param {string|number} input
 * @param {?number|false|module:formatCurrency~FormatCurrencyNumberOptions} options If pass number, it will be treated as precision.
 * @return {string}
 * @example
 * // Format with country default settings(currency symbol, precision, etc.).
 * formatCurrencyNumber('SG', 12345.6); // "12,345.60"
 * formatCurrencyNumber('SG', -2345.6); // "-2,345.60"
 * formatCurrencyNumber('ID', 12345.6); // "12.345"
 *
 * // Format with specific currency's settings(currency symbol, precision, etc.).
 * formatCurrencyNumber('IDR', '12345.6'); // '12.345'
 *
 * // Format without separating numbers
 * formatCurrencyNumber('ID', 12345.6, { noSeparator: true }); // "12345,60
 *
 * // Format with number of decimal digits(precision) specified, and overwrite country or currency's default configuration
 * formatCurrencyNumber('ID', 12345.6, 2); // "12.345,60"
 * formatCurrencyNumber('ID', 12345.6, { precision: 2 }); // "12.345,60"
 * formatCurrencyNumber('ID', 12345.6, { maxPrecision: 2 }); // "12.345,6"
 *
 * // Format without controlling number of deicimal digits(precision)
 * formatCurrencyNumber('IDR', 12345.6789, false);        // '12.345.6789'
 * formatCurrencyNumber('ID', 12345.6, { precision: false }); // "12.345,6"
 *
 * // Special case of using precision and maxPrecision together
 * formatCurrencyNumber('ID', 12345.6, { precision: 2, maxPrecision: 3 }); // "12.345,60"
 * formatCurrencyNumber('ID', 12345.6, { precision: 2, maxPrecision: 0 }); // "12.346"
 */
export function formatCurrencyNumber(
  code: Code,
  input: ParsableNumber,
  options?: FormatCurrencyNumberOptions
): ParsableNumber {
  const { precision, maxPrecision, noSeparator, separatorPattern } =
    typeof options === 'object'
      ? options
      : {
          precision: options,
          maxPrecision: undefined,
          noSeparator: undefined,
          separatorPattern: undefined,
        };
  const conf = getCurrencyConfig(code);
  let formattedNumber = conf
    ? formatNumber(input, {
        decimalPoint: conf.decimalPoint,
        separator: noSeparator ? void 0 : conf.separator,
        separatorPattern: noSeparator
          ? void 0
          : typeof separatorPattern !== 'undefined'
          ? separatorPattern
          : typeof conf.separatorPattern === 'undefined'
          ? DEFAULT_SEPARATOR_PATTERN
          : conf.separatorPattern,
        maxPrecision: ifNumberValidThen(
          maxPrecision,
          (p: number) => p,
          maxPrecision === false ? maxPrecision : conf.maxPrecision
        ),
        precision: ifNumberValidThen(
          precision,
          (p: number) => p,
          precision === false ? precision : conf.precision
        ),
      })
    : formatNumber(input);

  // special logic for Thailand currency display
  // should be removed whenever possible
  // if 123.1, still display as 123.10 if no precision set, but maxPrecision is set, this is purely for UI purpose, so that two item card, displays 123.10, 123.11 side by side
  // it will look more consistent, instead of 123.1 and 123.11
  const isTH = code === 'THB' || code === 'TH';
  const isDefaultTHCurrencySetting = precision === undefined;
  if (isTH && isDefaultTHCurrencySetting) {
    if (
      typeof formattedNumber === 'string' &&
      !!/\.\d$/.exec(formattedNumber)
    ) {
      formattedNumber += '0';
    }
  }

  return formattedNumber;
}

export type FormatCurrencyNumberOptions =
  | Precision
  | {
      symbol?: CurrencyConfig['symbol'];
      symbolPosition?: CurrencyConfig['symbolPosition'];
      precision?: Precision;
      maxPrecision?: Precision;
      noSeparator?: boolean;
      separatorPattern?: Readonly<number[]>;
      spaceBetweenSymbol?: CurrencyConfig['spaceBetweenSymbol'];
    };
/**
 * Format a price number into corresponding currency format with symbol.
 * @param  {string} code 	Can be country code or currency ID. If country code is provided, it will return this country's default currency setting.
 * @param  {string|number} input
 * @param  {?number|false|module:formatCurrency~FormatCurrencyNumberOptions} options
 * @return {string}               formatted currency string
 * @example
 * // Format with country default settings(currency symbol, precision, etc.).
 * formatCurrency('SG', 12345.6); // "$12,345.60"
 * formatCurrency('SG', -2345.6); // "$-2,345.60"
 * formatCurrency('ID', 12345.6); // "Rp12.345"
 *
 * // Format with specific currency's settings(currency symbol, precision, etc.).
 * formatCurrency('IDR', '12345.6'); // 'Rp12.345'
 *
 * // Format without separating numbers
 * formatCurrency('ID', 12345.6, { noSeparator: true }); // "Rp12345,60
 *
 * // Format with number of decimal digits(precision) specified, and overwrite country or currency's default configuration
 * formatCurrency('ID', 12345.6, 2); // "Rp12.345,60"
 * formatCurrency('ID', 12345.6, { precision: 2 }); // "Rp12.345,60"
 * formatCurrency('ID', 12345.6, { maxPrecision: 2 }); // "Rp12.345,6"
 *
 * // Format without controlling number of deicimal digits(precision)
 * formatCurrency('IDR', 12345.6789, false);        // 'Rp12.345.6789'
 * formatCurrency('ID', 12345.6, { precision: false }); // "Rp12.345,6"
 *
 * // Special case of using precision and maxPrecision together
 * formatCurrency('ID', 12345.6, { precision: 2, maxPrecision: 3 }); // "Rp12.345,60"
 * formatCurrency('ID', 12345.6, { precision: 2, maxPrecision: 0 }); // "Rp12.346"
 *
 * // Format without currency symbol (for replacing formatCurrencyNumber)
 * formatCurrency('SG', 12345.6, { symbol: false }); // "12,345.60"
 */
export function formatCurrency(
  code: Code,
  input: ParsableNumber,
  options?: FormatCurrencyNumberOptions
): string {
  const num = typeof input === 'number' ? input : parseFloat(input);
  const isNegative = num < 0;

  const formattedNumber = String(
    formatCurrencyNumber(code, Math.abs(num), options)
  );
  return _addCurrencySymbols(formattedNumber, isNegative, code, options);
}

function applyOptions(
  config: CurrencyConfig,
  options: FormatCurrencyNumberOptions | undefined
) {
  if (typeof options === 'object') {
    config = { ...config };
    if (typeof options.symbolPosition === 'string') {
      config.symbolPosition = options.symbolPosition;
    }
    if (config.symbolPosition === 'FRONT_AND_BACK') {
      if (
        Array.isArray(options.symbol) &&
        typeof options.symbol[0] === 'string' &&
        typeof options.symbol[1] === 'string'
      ) {
        config.symbol = options.symbol as [string, string];
      } else if (typeof options.symbol === 'string') {
        config.symbol = [options.symbol, options.symbol];
      }
      if (
        Array.isArray(options.spaceBetweenSymbol) &&
        typeof options.spaceBetweenSymbol[0] === 'boolean' &&
        typeof options.spaceBetweenSymbol[1] === 'boolean'
      ) {
        config.spaceBetweenSymbol = options.spaceBetweenSymbol as [
          boolean,
          boolean
        ];
      } else if (typeof options.spaceBetweenSymbol === 'boolean') {
        config.spaceBetweenSymbol = [
          options.spaceBetweenSymbol,
          options.spaceBetweenSymbol,
        ];
      }
    } else {
      if (typeof options.symbol === 'string') {
        config.symbol = options.symbol;
      }
      if (typeof options.spaceBetweenSymbol === 'boolean') {
        config.spaceBetweenSymbol = options.spaceBetweenSymbol;
      }
    }
  }
  return config;
}

/**
 * @private
 * @returns {string}
 */
export function _addCurrencySymbols(
  formattedNumber: string,
  isNegative: boolean,
  code: Code,
  options: FormatCurrencyNumberOptions | undefined
) {
  const config: CurrencyConfig = applyOptions(getCurrencyConfig(code), options);

  const sign = isNegative ? '-' : '';
  const space = (value: boolean | undefined) => (value ? ' ' : '');

  if (config.symbolPosition === SYMBOL_POSITION.FRONT_AND_BACK) {
    return (
      sign +
      config.symbol[0] +
      space(
        config.spaceBetweenSymbol[0] && !!config.symbol[0] && !!formattedNumber
      ) +
      formattedNumber +
      space(
        config.spaceBetweenSymbol[1] && !!config.symbol[1] && !!formattedNumber
      ) +
      config.symbol[1]
    );
  } else if (config.symbolPosition === SYMBOL_POSITION.BACK) {
    return (
      sign +
      formattedNumber +
      space(config.spaceBetweenSymbol && !!config.symbol && !!formattedNumber) +
      (config.symbol || '')
    );
  } else {
    return (
      sign +
      (config.symbol || '') +
      space(config.spaceBetweenSymbol && !!config.symbol && !!formattedNumber) +
      formattedNumber
    );
  }
}
